/**
 * useComponentLocationImages
 * Client-side counterpart of the guest API's `componentLocations`: for each
 * linked component derive the "Hitta komponenterna" images from the building
 * data (photo pins on parent photos, pins on the current level/roof drawing,
 * the pin on the current satellite image) and resolve their Storage URLs.
 * Raster only: the vector drawing overlay the guest page draws is not used here.
 */
import { useEffect, useMemo, useState } from 'react';
import { Building, BaseComponent, BlueprintLocation, LevelModel, PhotoModel } from '../../types/protocols';
import { photoLocationList } from '../../Helpers/photoLocations';
import { roomDrawingMarker } from '../../Helpers/componentRoomMarker';
import { resolveFirebaseImageUrl } from '../../Helpers/ImageLoader';
import { ComponentLocationImage } from '../../Pages/WorkOrders/components/shared';

interface Marker {
    storagePath: string;
    label: string;
    x: number;
    y: number;
    refWidth: number;
    refHeight: number;
}

const currentDrawing = (
    drawing: { currentBlueprintKey?: string; blueprints?: { [key: string]: PhotoModel } } | undefined
): { key: string; image: PhotoModel } | null => {
    const key = drawing?.currentBlueprintKey;
    const image = key ? drawing?.blueprints?.[key] : undefined;
    return key && image?.storagePath ? { key, image } : null;
};

const toMarker = (loc: BlueprintLocation, image: PhotoModel, label: string): Marker => ({
    storagePath: image.storagePath,
    label,
    x: loc.x || 0,
    y: loc.y || 0,
    refWidth: loc.width || image.width || 0,
    refHeight: loc.height || image.height || 0,
});

/**
 * Markers for one component, in the guest page's order: photos, drawings, map.
 * A component in a room with no pin of its own on the room's level drawing
 * gets the room's pin there, captioned with the room's name.
 */
export const componentLocationMarkers = (
    component: BaseComponent,
    building: Building
): Marker[] => {
    const markers: Marker[] = [];

    for (const { location } of photoLocationList(component, building)) {
        markers.push({
            storagePath: location.storagePath,
            label: 'Plats på foto',
            x: location.x || 0,
            y: location.y || 0,
            refWidth: location.width || 0,
            refHeight: location.height || 0,
        });
    }

    const structure = building.model.structure;
    for (const [blueprintKey, loc] of Object.entries(component.blueprintLocations || {})) {
        const level = Object.values(structure?.levels || {}).find(
            (l: LevelModel) => currentDrawing(l)?.key === blueprintKey
        );
        const levelDrawing = currentDrawing(level);
        if (level && levelDrawing) {
            markers.push(toMarker(loc, levelDrawing.image, level.name || 'Ritning'));
            continue;
        }
        const roof = currentDrawing(structure?.roofPlan);
        if (roof && roof.key === blueprintKey) {
            markers.push(toMarker(loc, roof.image, 'Takplan'));
        }
    }

    const roomMarker = roomDrawingMarker(component, building);
    if (roomMarker) {
        markers.push(toMarker(roomMarker.location, roomMarker.image, roomMarker.roomLabel));
    }

    const mapKey = building.model.currentMapImageKey;
    const mapLoc = mapKey ? component.mapLocations?.[mapKey] : undefined;
    const mapImage = mapKey ? building.model.mapImages?.[mapKey] : undefined;
    if (mapLoc && mapImage?.storagePath) {
        markers.push(toMarker(mapLoc, mapImage, 'Karta'));
    }

    return markers;
};

export function useComponentLocationImages(
    building: Building | null,
    components: Record<string, BaseComponent>,
    componentKeys: string[]
): Record<string, ComponentLocationImage[]> {
    const markersByKey = useMemo(() => {
        if (!building) return {} as Record<string, Marker[]>;
        const out: Record<string, Marker[]> = {};
        for (const key of componentKeys) {
            const component = components[key];
            if (component) out[key] = componentLocationMarkers(component, building);
        }
        return out;
    }, [building, components, componentKeys]);

    const storagePaths = useMemo(
        () => Array.from(new Set(Object.values(markersByKey).flat().map((m) => m.storagePath))),
        [markersByKey]
    );
    const [urls, setUrls] = useState<Record<string, string | null>>({});

    useEffect(() => {
        let cancelled = false;
        const missing = storagePaths.filter((p) => !(p in urls));
        if (missing.length === 0) return;
        missing.forEach(async (path) => {
            let url: string | null = null;
            try {
                url = await resolveFirebaseImageUrl(path);
            } catch (err) {
                console.warn('Could not resolve location image', path, err);
            }
            if (!cancelled) setUrls((prev) => ({ ...prev, [path]: url }));
        });
        return () => { cancelled = true; };
    }, [storagePaths, urls]);

    return useMemo(() => {
        const out: Record<string, ComponentLocationImage[]> = {};
        for (const [key, markers] of Object.entries(markersByKey)) {
            out[key] = markers.map((m) => {
                const hasFrame = m.refWidth > 0 && m.refHeight > 0;
                return {
                    label: m.label,
                    imageUrl: urls[m.storagePath] ?? null,
                    loading: !(m.storagePath in urls),
                    x: hasFrame ? m.x / m.refWidth : NaN,
                    y: hasFrame ? m.y / m.refHeight : NaN,
                    aspectRatio: hasFrame ? m.refWidth / m.refHeight : undefined,
                };
            });
        }
        return out;
    }, [markersByKey, urls]);
}
